// â”€â”€â”€ Admin Setup Page (superadmin-only) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Browser-based alternative to scripts/seed-feature-flags.ts.
//
// Use when you can't run the local script (no service account JSON, no Node,
// no terminal access). Visit /admin/setup after logging in as superadmin â†’
// click "Seed missing flags" â†’ done.
//
// Same Firestore rules apply: only superadmin can WRITE to `_config/`. Reads
// permitted for any signed-in user, but UI hides controls if not superadmin.

import React, { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

// KEEP IN SYNC with src/lib/featureFlags.ts CANONICAL_FLAGS + scripts/seed-feature-flags.ts
const CANONICAL_FLAGS: string[] = [
  'contacts.enabled',
  'contacts.needsReviewQueue',
  'contacts.useContactsForLogin',
  'contacts.waDispatch',
  'epr.useContactsDirectory',
  'okr.useContactsDirectory',
];

const FLAG_DESCRIPTIONS: Record<string, string> = {
  'contacts.enabled':
    'Phase 1+ master switch â€” enables the new contacts directory UI and writes.',
  'contacts.needsReviewQueue':
    'Phase 2 â€” shows the "Needs Review" tab in Account Portal contacts page.',
  'contacts.useContactsForLogin':
    'Phase 6 â€” userless OTP login surfaces (/my-reviews, /my-feedback, etc).',
  'contacts.waDispatch':
    'Phase 8 â€” enables wa.me click-to-send dispatcher batch UI.',
  'epr.useContactsDirectory':
    'Phase 11 â€” EPR reads contacts/ instead of users/ for identity.',
  'okr.useContactsDirectory':
    'Phase 12 â€” OKR Tracker reads contacts/ for cascade picker + KR ownership.',
};

const FLAGS_DOC = doc(db, '_config', 'featureFlags');

type FlagsDoc = Record<string, any>;

const AdminSetupPage: React.FC = () => {
  const { role, firebaseUser } = useAuth();
  const isSuperadmin = role === 'superadmin';

  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState<FlagsDoc>({});
  const [docExists, setDocExists] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(FLAGS_DOC);
      setDocExists(snap.exists());
      setFlags(snap.exists() ? (snap.data() ?? {}) : {});
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }

  async function seedMissing() {
    if (!isSuperadmin) return;
    setBusy('seed');
    setError(null);
    try {
      const toAdd: Record<string, any> = {};
      for (const key of CANONICAL_FLAGS) {
        if (flags[key] === undefined) toAdd[key] = false;
      }
      if (Object.keys(toAdd).length === 0) {
        await refresh();
        return;
      }
      await setDoc(
        FLAGS_DOC,
        { ...toAdd, _lastSeededAt: serverTimestamp() },
        { merge: true },
      );
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function toggleFlag(key: string, next: boolean) {
    if (!isSuperadmin) return;
    setBusy(key);
    setError(null);
    try {
      await setDoc(
        FLAGS_DOC,
        { [key]: next, _lastUpdatedAt: serverTimestamp() },
        { merge: true },
      );
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  const missingFlags = CANONICAL_FLAGS.filter((k) => flags[k] === undefined);
  const unknownFlags = Object.keys(flags).filter(
    (k) => !k.startsWith('_') && !CANONICAL_FLAGS.includes(k),
  );

  const lastSeededAt: Timestamp | undefined = flags._lastSeededAt;
  const lastUpdatedAt: Timestamp | undefined = flags._lastUpdatedAt;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-stone-900">Admin Setup</h1>
        <p className="text-sm text-stone-500 mt-1">
          Manage feature flags for the Contacts Directory rollout. Toggles
          require <strong>superadmin</strong> role.
        </p>
      </div>

      {/* Status */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-stone-500">
            Signed in as
          </span>
          <span className="text-sm font-medium text-stone-800">
            {firebaseUser?.email ?? 'â€”'}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              isSuperadmin
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            }`}
          >
            {role ?? 'no role'}
          </span>
        </div>
        {!isSuperadmin && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            You can VIEW flags but cannot CHANGE them. Ask a superadmin to grant
            you the role, or sign in with a superadmin account.
          </p>
        )}
      </div>

      {/* Doc status */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-stone-900">
              <code className="text-sm bg-stone-100 px-1.5 py-0.5 rounded">
                /_config/featureFlags
              </code>
            </h2>
            <p className="text-xs text-stone-500 mt-1">
              Status:{' '}
              {loading ? (
                'loadingâ€¦'
              ) : docExists ? (
                <span className="text-emerald-700">document exists</span>
              ) : (
                <span className="text-amber-700">document does NOT exist</span>
              )}
            </p>
            {lastSeededAt && (
              <p className="text-xs text-stone-400 mt-0.5">
                Last seeded: {lastSeededAt.toDate().toLocaleString()}
              </p>
            )}
            {lastUpdatedAt && (
              <p className="text-xs text-stone-400 mt-0.5">
                Last updated: {lastUpdatedAt.toDate().toLocaleString()}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={refresh}
            className="text-xs px-3 py-1.5 rounded border border-stone-200 text-stone-700 hover:bg-stone-50"
          >
            Refresh
          </button>
        </div>

        {missingFlags.length > 0 && isSuperadmin && (
          <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded">
            <p className="text-sm text-amber-800">
              {missingFlags.length} canonical flag(s) missing.
            </p>
            <button
              type="button"
              onClick={seedMissing}
              disabled={busy !== null}
              className="text-sm px-4 py-1.5 bg-[#011132] hover:bg-[#022350] text-white rounded font-medium disabled:opacity-50"
            >
              {busy === 'seed' ? 'Seedingâ€¦' : `Seed ${missingFlags.length} missing`}
            </button>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}
      </div>

      {/* Flag list */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-100 bg-stone-50">
          <h2 className="text-sm font-semibold text-stone-700">
            Feature flags ({CANONICAL_FLAGS.length} canonical)
          </h2>
        </div>
        <ul className="divide-y divide-stone-100">
          {CANONICAL_FLAGS.map((key) => {
            const present = key in flags;
            const value = flags[key];
            const on = value === true;
            return (
              <li key={key} className="px-5 py-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <code className="text-sm font-medium text-stone-900">{key}</code>
                  {FLAG_DESCRIPTIONS[key] && (
                    <p className="text-xs text-stone-500 mt-1">
                      {FLAG_DESCRIPTIONS[key]}
                    </p>
                  )}
                  {!present && (
                    <p className="text-xs text-amber-700 mt-1">
                      âš  Missing â€” click "Seed missing" above to add as <code>false</code>.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-xs px-2 py-1 rounded font-medium ${
                      !present
                        ? 'bg-stone-100 text-stone-400'
                        : on
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-stone-100 text-stone-600'
                    }`}
                  >
                    {!present ? 'â€”' : on ? 'ON' : 'off'}
                  </span>
                  {isSuperadmin && present && (
                    <button
                      type="button"
                      onClick={() => toggleFlag(key, !on)}
                      disabled={busy !== null}
                      className={`text-xs px-3 py-1.5 rounded border font-medium disabled:opacity-50 ${
                        on
                          ? 'border-stone-300 text-stone-700 hover:bg-stone-50'
                          : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {busy === key ? 'â€¦' : on ? 'Turn off' : 'Turn on'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Drift */}
      {unknownFlags.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 p-5 space-y-2">
          <h2 className="text-sm font-semibold text-amber-800">
            Unknown flags ({unknownFlags.length})
          </h2>
          <p className="text-xs text-stone-500">
            Found in the doc but not in the canonical list. These may be from
            an older deployment or a typo. Not modified by this page.
          </p>
          <ul className="text-xs space-y-1 mt-2">
            {unknownFlags.map((k) => (
              <li key={k} className="font-mono text-stone-700">
                {k} = {String(flags[k])}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* M365 sync (Phase 13) */}
      {isSuperadmin && <M365SyncSection />}
    </div>
  );
};

// ─── M365 sync section ───────────────────────────────────────────────────────

const M365SyncSection: React.FC = () => {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [result, setResult]   = useState<any>(null);

  async function handleSync() {
    if (!window.confirm(
      'Run M365 sync now? Fetches all active users from Azure AD into contactsStaging/. ' +
      'Then review staging diff and promote to contacts/. Safe to run multiple times.',
    )) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'syncContactsFromM365');
      const res = await fn({});
      setResult(res.data);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handlePromote() {
    if (!window.confirm(
      'Promote contactsStaging/ → contacts/? Applies the latest M365 sync diff. ' +
      'Existing contacts.manualEdits[] fields are respected (admin overrides win).',
    )) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'promoteContactsFromStaging');
      const res = await fn({});
      setResult(res.data);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
      <div>
        <h2 className="text-base font-semibold text-stone-900">Microsoft 365 Sync</h2>
        <p className="text-xs text-stone-500 mt-1">
          Phase 13 — pull active employees from Azure AD into <code>contactsStaging/</code>, then promote diff to <code>contacts/</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleSync}
          disabled={busy}
          className="px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50 bg-blue-700 hover:bg-blue-800"
        >
          <div className="flex items-center justify-center gap-2">
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>sync</span>
            {busy ? 'Working…' : '1. Sync from M365 → staging'}
          </div>
        </button>

        <button
          type="button"
          onClick={handlePromote}
          disabled={busy}
          className="px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50 bg-emerald-700 hover:bg-emerald-800"
        >
          <div className="flex items-center justify-center gap-2">
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>publish</span>
            {busy ? 'Working…' : '2. Promote staging → contacts'}
          </div>
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {result && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
          <p className="font-semibold text-emerald-900 mb-1">✅ Success</p>
          <pre className="text-emerald-800 overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}

      <p className="text-[11px] text-stone-400">
        Workflow: click <strong>1</strong> to fetch from Azure AD (writes to <code>contactsStaging/</code>, safe to re-run). Review the staging entries in Firestore Console. Then click <strong>2</strong> to apply the diff to <code>contacts/</code>.
      </p>
    </div>
  );
};

export default AdminSetupPage;
